import React from "react"
import {useTranslation} from "react-i18next"
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from "@floating-ui/react"
import {CheckIcon, ChevronDoubleUpIcon, ChevronDownIcon, MinusIcon} from "@heroicons/react/20/solid"
import {twMerge} from "tailwind-merge"

import {useKlaraUpdateDealPriorityMutation} from "../../../queries/klaraDashboard.ts"
import {AKlaraDashboardDeal, ASalesAssistantDealPriorities} from "../../../services/types.generated.ts"
import {enumTranslKey} from "../../../utils/i18n"
import {stopRowEvents} from "./stopRowEvents.ts"

const PRIORITIES = [
  ASalesAssistantDealPriorities.High,
  ASalesAssistantDealPriorities.Medium,
  ASalesAssistantDealPriorities.Low,
] as const

const PRIORITY_BADGE: Record<ASalesAssistantDealPriorities, {className: string; Icon: typeof MinusIcon}> = {
  [ASalesAssistantDealPriorities.High]: {
    className: "border-cr-blue-dark bg-cr-blue-dark text-cr-white",
    Icon: ChevronDoubleUpIcon,
  },
  [ASalesAssistantDealPriorities.Medium]: {
    className: "border-cr-blue-light bg-cr-slate-8 text-cr-blue-dark",
    Icon: MinusIcon,
  },
  [ASalesAssistantDealPriorities.Low]: {
    className: "border-cr-slate-15 bg-cr-white text-cr-slate-60",
    Icon: ChevronDownIcon,
  },
}

/** The dot beside each priority in the menu and the priority filter. */
export const PRIORITY_DOT_CLASS_NAME: Record<ASalesAssistantDealPriorities, string> = {
  [ASalesAssistantDealPriorities.High]: "bg-cr-red",
  [ASalesAssistantDealPriorities.Medium]: "bg-cr-yellow-dark",
  [ASalesAssistantDealPriorities.Low]: "bg-cr-slate-40",
}

/** The lead's priority badge; clicking it opens a menu that sets the priority by hand. */
export const PriorityMenu: React.FC<{row: Pick<AKlaraDashboardDeal, "id" | "priority">}> = ({row}) => {
  const {t} = useTranslation()
  const {mutate, isPending} = useKlaraUpdateDealPriorityMutation()
  const [isOpen, setIsOpen] = React.useState(false)
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null)
  const listRef = React.useRef<Array<HTMLElement | null>>([])
  const selectedIndex = PRIORITIES.indexOf(row.priority)

  const {context, refs, floatingStyles} = useFloating({
    placement: "bottom-start",
    middleware: [flip(), offset(4), shift({padding: 16})],
    open: isOpen,
    // While a change is saving the menu stays shut, so a second pick cannot be sent.
    onOpenChange: open => setIsOpen(open && !isPending),
    whileElementsMounted: autoUpdate,
  })

  const {getReferenceProps, getFloatingProps, getItemProps} = useInteractions([
    useClick(context),
    useDismiss(context),
    useRole(context, {role: "menu"}),
    useListNavigation(context, {listRef, activeIndex, selectedIndex, onNavigate: setActiveIndex}),
  ])

  const select = (priority: ASalesAssistantDealPriorities) => {
    setIsOpen(false)
    mutate({dealId: row.id, priority})
  }

  const priorityLabel = (priority: ASalesAssistantDealPriorities) =>
    t(enumTranslKey("SalesAssistantDealPriority", priority))

  return (
    <div className={"inline-flex"} {...stopRowEvents}>
      <button
        ref={refs.setReference}
        type={"button"}
        // Not natively disabled: the menu returns focus here on close, and a disabled button would drop it.
        aria-disabled={isPending}
        aria-label={t("KlaraDashboard_Leads_Priority_Change", {priority: priorityLabel(row.priority)})}
        className={twMerge(
          "inline-flex items-center gap-[5px] rounded-md border py-[3px] pr-[9px] pl-[7px] text-xs font-semibold transition-colors hover:border-cr-blue-mid",
          PRIORITY_BADGE[row.priority].className,
          isPending ? "cursor-wait opacity-50" : "cursor-pointer"
        )}
        {...getReferenceProps()}
      >
        <PriorityIcon priority={row.priority} />
        {priorityLabel(row.priority)}
      </button>

      {isOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              aria-label={t("KlaraDashboard_Leads_Priority_MenuTitle")}
              className={
                "z-100 flex min-w-36 flex-col gap-0.5 rounded-[10px] border border-cr-blue-light bg-cr-white p-1 shadow-lg pixel-units"
              }
              {...getFloatingProps()}
            >
              <span
                aria-hidden={"true"}
                className={"px-[9px] pt-1.5 pb-1 text-[10px] font-bold tracking-[0.8px] text-cr-slate-60 uppercase"}
              >
                {t("KlaraDashboard_Leads_Priority_MenuTitle")}
              </span>
              {PRIORITIES.map((priority, index) => (
                <button
                  key={priority}
                  ref={node => {
                    listRef.current[index] = node
                  }}
                  type={"button"}
                  role={"menuitemradio"}
                  aria-checked={priority === row.priority}
                  tabIndex={activeIndex === index ? 0 : -1}
                  className={twMerge(
                    "flex items-center gap-2 rounded-[7px] px-[9px] py-[7px] text-left text-xs font-semibold text-cr-slate-90 hover:bg-cr-slate-4 focus:bg-cr-slate-4 focus:outline-hidden",
                    priority === row.priority && "bg-cr-blue-super-light"
                  )}
                  {...getItemProps({onClick: () => select(priority)})}
                >
                  <span aria-hidden className={twMerge("size-2 rounded-full", PRIORITY_DOT_CLASS_NAME[priority])} />
                  {priorityLabel(priority)}
                  {priority === row.priority && <CheckIcon className={"ml-auto size-4 text-cr-blue"} aria-hidden />}
                </button>
              ))}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </div>
  )
}

const PriorityIcon: React.FC<{priority: ASalesAssistantDealPriorities}> = ({priority}) => {
  const {Icon} = PRIORITY_BADGE[priority]

  return <Icon className={"size-3 shrink-0"} aria-hidden={"true"} />
}
